import { useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  MailThreadItemFieldsFragment,
  MailThreadsPageDocument,
  MailThreadsPageQuery,
  MailThreadsPageQueryVariables,
  MailThreadViewQuery,
  useMailRevisionCheckQuery,
  useMailThreadViewQuery,
} from 'core/graphql';
import { usePageVisiblePoll } from 'core/hooks/Meta/usePageVisiblePoll';

import { CursorFetcher, useCursorList } from './useCursorList';
import { MailViewKey, mailViewEnum } from './mailModel';
import { isMailCategory } from './mailAiModel';

export type IMailThreadItem = MailThreadItemFieldsFragment;
export type IMailThreadDetail = NonNullable<MailThreadViewQuery['mailThread']>;

export const MAIL_THREADS_PAGE = 50;

interface IThreadFilter {
  /** `null` — все мои ящики. */
  mailboxIds: string[] | null;
  view: MailViewKey;
  labelId?: string | null;
  search?: string;
  contactEmail?: string | null;
  /** Категория ИИ (`STUDY`, …). */
  category?: string | null;
}

/**
 * ТЗ 0122 — список тредов по курсору (мимо кеша Apollo, см.
 * `useCursorList`). `reload()` — после действия и при новой ревизии.
 */
export const useMailThreadList = (filter: IThreadFilter, skip = false) => {
  const client = useApolloClient();
  const { mailboxIds, view, labelId, search, contactEmail, category } = filter;
  const key = JSON.stringify([
    mailboxIds,
    view,
    labelId || null,
    (search || '').trim(),
    contactEmail || null,
    isMailCategory(category) ? category.toUpperCase() : null,
  ]);
  const fetcher = useCallback<CursorFetcher<IMailThreadItem>>(
    async (cursor, limit) => {
      const [ids, v, label, q, contact, cat] = JSON.parse(key);
      const { data } = await client.query<
        MailThreadsPageQuery,
        MailThreadsPageQueryVariables
      >({
        query: MailThreadsPageDocument,
        variables: {
          mailboxIds: ids,
          view: mailViewEnum(v),
          labelId: label,
          search: q || null,
          contactEmail: contact,
          category: cat,
          cursor,
          limit,
        },
        fetchPolicy: 'no-cache',
      });
      return {
        items: data.mailThreads.items,
        nextCursor: data.mailThreads.nextCursor ?? null,
        total: data.mailThreads.total ?? null,
      };
    },
    [client, key],
  );
  return useCursorList(key, fetcher, MAIL_THREADS_PAGE, skip);
};

/** Открытый тред: письма (тела старых сервер догружает), журнал, связи. */
export const useMailThread = (id: string | null, fresh = false) => {
  const { data, loading, error, refetch } = useMailThreadViewQuery({
    variables: { id: id || '' },
    skip: !id,
    // `fresh` — окно задачи: поля из свежего разбора, а не из кеша.
    fetchPolicy: fresh ? 'network-only' : 'cache-and-network',
  });
  const thread = data?.mailThread ?? null;
  const reload = useCallback(() => {
    if (!id) return;
    refetch().catch(() => undefined);
  }, [id, refetch]);
  return {
    thread: thread && thread.id === id ? thread : null,
    loading: loading && !thread,
    // Нет доступа и нет треда — сервер отвечает `null` без ошибки.
    missing: Boolean(id) && !loading && !error && Boolean(data) && !thread,
    failed: Boolean(error) && !thread,
    /** Ответ сети ещё не пришёл (данные могут быть из кеша). */
    refreshing: loading,
    reload,
  };
};

export const MAIL_REVISION_POLL_MS = 15000;

/**
 * Живость (§5): ревизия ящиков раз в 15 с, пока вкладка видна; изменилась —
 * `onChange` (перечитать список и открытый тред). Первый ответ — не
 * изменение.
 */
export const useMailRevision = (
  mailboxIds: string[] | null,
  onChange: () => void,
  enabled = true,
  intervalMs: number = MAIL_REVISION_POLL_MS,
) => {
  const {
    data,
    startPolling,
    stopPolling,
    refetch,
  } = useMailRevisionCheckQuery({
    variables: { mailboxIds },
    skip: !enabled,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });
  const refetchVisible = useCallback(() => refetch(), [refetch]);
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    intervalMs,
    enabled,
    refetchVisible,
  );
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const revision = data?.mailRevision.revision ?? null;
  const scope = JSON.stringify(mailboxIds);
  const last = useRef<{ scope: string; revision: string | null }>({
    scope,
    revision: null,
  });
  useEffect(() => {
    if (!revision) return;
    const prev = last.current;
    last.current = { scope, revision };
    if (prev.scope === scope && prev.revision && prev.revision !== revision) {
      onChangeRef.current();
    }
  }, [revision, scope]);
  return { inboxUnread: data?.mailRevision.inboxUnread ?? null };
};

const NOOP = () => undefined;
const MENU_POLL_MS = 60000;

/**
 * Бейдж «Почта» в меню: непрочитанные во входящих всех моих ящиков. Опрос
 * реже (раз в минуту) — меню висит на каждой странице.
 */
export const useMailInboxUnread = (enabled: boolean) => {
  const { inboxUnread } = useMailRevision(null, NOOP, enabled, MENU_POLL_MS);
  return enabled ? inboxUnread ?? 0 : 0;
};
